import { useEffect, useRef, useState } from "react";
import { Smile } from "lucide-react";

/**
 * Picker de emoji simples, zero-dep. Lista curada de emojis comuns por categoria.
 * Posiciona o popover por cima do botão (ideal pra composer fixo no rodapé).
 *
 * Uso:
 *   <EmojiPicker onSelect={(e) => inserirNaTextarea(e)} disabled={editing} />
 */
const CATEGORIAS: Array<{ id: string; label: string; emojis: string[] }> = [
  {
    id: "rostos",
    label: "😀",
    emojis: [
      "😀","😃","😄","😁","😆","😅","🤣","😂","🙂","🙃","😉","😊","😇","🥰","😍","🤩",
      "😘","😗","☺️","😚","😙","🥲","😋","😛","😜","🤪","😝","🤑","🤗","🤭","🤫","🤔",
      "😐","😑","😶","😏","😒","🙄","😬","😮‍💨","🤥","😌","😔","😪","🤤","😴","😷","🤒",
      "🥱","😎","🤓","🧐","🥺","😢","😭","😤","😠","😡","🤬","🤯","🥵","🥶","😱","😨",
    ],
  },
  {
    id: "maos",
    label: "👍",
    emojis: [
      "👍","👎","👌","🤌","🤏","✌️","🤞","🤟","🤘","🤙","👈","👉","👆","🖕","👇","☝️",
      "👋","🤚","🖐️","✋","🖖","👏","🙌","🙏","🤝","💪","🫶","🫰","🫵","✊","🤛","🤜",
      "🦾","🖖","💅","👀","👁️","🧠","🫀","🫁","🦷","🦴","👃","👂","🦻","👅","👄","🫦",
    ],
  },
  {
    id: "coracoes",
    label: "❤️",
    emojis: [
      "❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❣️","💕","💞","💓","💗","💖",
      "💘","💝","💟","♥️","🫶","💯","💢","💥","💫","💦","💨","🕳️","💣","💬","👁️‍🗨️","🗨️",
      "🗯️","💭","💤","✨","🎉","🎊","🥂","🍾","🎁","🎂","🌹","🌷","🌸","💐","🌺","🌻",
    ],
  },
  {
    id: "objetos",
    label: "💼",
    emojis: [
      "💼","📁","📂","📅","📆","🗒️","🗓️","📇","📈","📉","📊","📋","📌","📍","📎","🖇️",
      "📏","📐","✂️","🗃️","🗄️","🗑️","🔒","🔓","🔏","🔐","🔑","🗝️","🔨","⛏️","⚒️","🛠️",
      "💻","🖥️","🖨️","⌨️","🖱️","🖲️","💽","💾","💿","📀","📱","☎️","📞","📟","📠","📺",
      "📷","📹","🎥","🔍","🔎","🔬","🔭","💡","🔦","🕯️","🧯","🛢️","💸","💵","💴","💶",
      "💷","🪙","💰","💳","🧾","✉️","📧","📨","📩","📤","📥","📦","📪","📫","📬","📭",
    ],
  },
  {
    id: "tempo",
    label: "⏰",
    emojis: [
      "⏰","⏱️","⏲️","🕐","🕑","🕒","🕓","🕔","🕕","🕖","🕗","🕘","🕙","🕚","🕛","🗓️",
      "✅","☑️","✔️","❌","❎","⚠️","🚫","⛔","✳️","❇️","⭐","🌟","💫","🔥","💯","♻️",
      "🆕","🆗","🆙","🆒","🆓","🔝","🔚","🔛","🔜","🆎","ℹ️","🔔","🔕","📣","📢","📡",
    ],
  },
  {
    id: "comida",
    label: "🍔",
    emojis: [
      "🍔","🍟","🍕","🌭","🥪","🌮","🌯","🥙","🧆","🥗","🍱","🍣","🍤","🍙","🍘","🍚",
      "🍜","🍲","🥘","🥣","🍝","🥫","🍞","🥐","🥖","🫓","🥨","🥯","🧀","🍳","🥞","🧈",
      "🍖","🍗","🥩","🥓","🍦","🍧","🍨","🍩","🍪","🎂","🍰","🧁","🥧","🍫","🍬","🍭",
      "☕","🍵","🥤","🧋","🧃","🍶","🍾","🍷","🍸","🍹","🍺","🍻","🥂","🥃","🥛","🧉",
    ],
  },
  {
    id: "natureza",
    label: "🌳",
    emojis: [
      "🐶","🐱","🐭","🐹","🐰","🦊","🐻","🐼","🐻‍❄️","🐨","🐯","🦁","🐮","🐷","🐸","🐵",
      "🦄","🐔","🐧","🐦","🐤","🦆","🦅","🦉","🦇","🐺","🐗","🐴","🦓","🦒","🐘","🦏",
      "🌳","🌲","🌴","🌵","🌿","☘️","🍀","🍃","🌱","🌾","💐","🌷","🌹","🥀","🌺","🌸",
      "🌼","🌻","☀️","🌤️","⛅","🌥️","☁️","🌦️","🌧️","⛈️","🌩️","🌨️","❄️","☃️","⛄","🌬️",
    ],
  },
];

export function EmojiPicker({ onSelect, disabled }: { onSelect: (emoji: string) => void; disabled?: boolean }) {
  const [aberto, setAberto] = useState(false);
  const [aba, setAba] = useState(0);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Fecha ao clicar fora ou Esc
  useEffect(() => {
    if (!aberto) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setAberto(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setAberto(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [aberto]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setAberto((v) => !v)}
        disabled={disabled}
        className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-30"
        title="Inserir emoji"
        aria-label="Inserir emoji"
      >
        <Smile size={18} />
      </button>

      {aberto && (
        <div
          ref={popRef}
          className="absolute bottom-full mb-2 left-0 z-30 w-[320px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl p-2"
        >
          {/* Tabs */}
          <div className="flex items-center gap-0.5 border-b border-slate-200 dark:border-slate-700 pb-1.5 mb-1.5">
            {CATEGORIAS.map((c, i) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setAba(i)}
                className={`flex-1 text-base py-1 rounded transition-colors ${
                  aba === i
                    ? "bg-blue-50 dark:bg-blue-900/30"
                    : "hover:bg-slate-50 dark:hover:bg-slate-700/50"
                }`}
                title={c.id}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-8 gap-0.5 max-h-[200px] overflow-y-auto kanban-scroll">
            {CATEGORIAS[aba].emojis.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => {
                  onSelect(e);
                  // Mantém aberto pra usuário inserir vários — fecha só com clique fora ou Esc.
                }}
                className="text-xl leading-none p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
